/**
 * Every FlatBuffer schema this page has met that is not its own, and how it
 * resolved: what the admin skew banner and the master-admin inspector show.
 *
 * Observations are keyed by channel, schema and type, so a schema met on every
 * frame is one entry whose count grows, and the store stays small.
 */

/** Where a schema was met. */
export type SkewChannel = "http" | "ws";

/**
 * The outcome of a schema the server could not compare but whose type this page
 * proved, from the server's own schema, is laid out as in its own. The skew
 * banner leaves such entries out; the inspector lists them.
 */
export const LOCAL_PROOF_OUTCOME = "proven-locally";

/** One schema met on one channel, for one type, and how it resolved. */
export interface SkewObservation {
  channel: SkewChannel;
  /** The writer's schema, as 64 lower-case hex digits. */
  schema: string;
  /** The type read (`Api.PaginatedBanks`, `BatchedFrontEnd.BatchedFrontEndMessage`). */
  typeName: string;
  /** How it resolved: a schema discovery outcome, or `unknown` when the server could not say. */
  outcome: string;
  /** Where the server got the writer's schema (`compiled`, `ci-signed`, …), when known. */
  provenance?: string;
  /** What the schema's registry signature says (`verified`, `unsigned`, …), when it was fetched. */
  signature?: string;
  /** How many times it was met. */
  count: number;
  /** When it was last met (ms since the epoch). */
  lastSeen: number;
}

/** One monitoring frame of a schema not this page's own, kept for the inspector. */
export interface SkewFrame {
  /** The schema its envelope names. */
  schema: string;
  /** Its root type, as the schema API named it; empty when it could not. */
  typeName: string;
  /** The FlatBuffer it carried: a size-prefixed batch, or one message. */
  payload: Uint8Array;
  /** How the page read it: `all`, `except` (some messages left out) or `none`. */
  read: string;
  /** When it arrived (ms since the epoch). */
  receivedAt: number;
}

/** How many such frames are kept, newest last. */
const MAX_FRAMES = 25;

type Listener = () => void;

let observations: readonly SkewObservation[] = [];
let frames: readonly SkewFrame[] = [];
const listeners = new Set<Listener>();

/** Records one meeting with a schema that is not this page's own. */
export function noteSchemaSkew(seen: Omit<SkewObservation, "count" | "lastSeen">): void {
  const lastSeen = Date.now();
  const index = observations.findIndex(
    (o) => o.channel === seen.channel && o.schema === seen.schema && o.typeName === seen.typeName,
  );
  if (index === -1) {
    observations = [...observations, { ...seen, count: 1, lastSeen }];
  } else {
    const next = [...observations];
    next[index] = { ...next[index], ...seen, count: next[index].count + 1, lastSeen };
    observations = next;
  }
  listeners.forEach((listener) => listener());
}

/** Keeps `frame`, dropping the oldest past the last {@link MAX_FRAMES}. */
export function noteSkewFrame(frame: Omit<SkewFrame, "receivedAt">): void {
  frames = [...frames.slice(1 - MAX_FRAMES), { ...frame, receivedAt: Date.now() }];
  listeners.forEach((listener) => listener());
}

/** The frames kept, oldest first; a new array whenever one is kept (for `useSyncExternalStore`). */
export function skewFramesSnapshot(): readonly SkewFrame[] {
  return frames;
}

/** Every observation so far; a new array whenever one changes (for `useSyncExternalStore`). */
export function schemaSkewSnapshot(): readonly SkewObservation[] {
  return observations;
}

/** Calls `listener` after every change; returns the unsubscribe function. */
export function subscribeSchemaSkew(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Forgets every observation and frame (for tests). */
export function resetSchemaSkew(): void {
  observations = [];
  frames = [];
  listeners.forEach((listener) => listener());
}
